import { el, fmtInt, fmtCompact, fmtDay, relativeTime } from '../ui/dom';
import { renderShell, renderError } from '../ui/shell';
import { kpiCard, kpiGrid, panel, panelGrid, segmented, caption } from '../ui/components';
import { lineAreaChart, barList, stackedBar, funnelChart, type Segment } from '../ui/charts';
import {
  getDashboardOverview,
  getScanStats,
  getSessionStats,
  getFunnels,
  getCohorts,
  getSubscriptionBreakdown,
  getDailySeries,
  getAnalyticsFreshness,
  type DashboardOverview,
  type ScanStats,
  type SessionStats,
  type FunnelResult,
  type Cohort,
  type SubscriptionBreakdown,
  type DailySeriesMetric,
  type AnalyticsFreshness,
} from '../api/analytics';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import { resolveEmptyPanelState, type EmptyPanelState } from './app-analytics-empty-state';
import { liveAnalyticsSection } from './app-live-analytics';

const SUB_COLORS: Record<string, string> = {
  free: '#8e8e93',
  pro: '#0071e3',
  business: '#5e5ce6',
};

function loadingBox(): HTMLElement {
  return el('div', { class: 'loading' }, ['Loading…']);
}

/** Result of settle(): distinguishes "succeeded with no data" from "the
 * request actually failed" — collapsing those two was the root cause of the
 * false "no data" empty state (see app-analytics-empty-state.ts). */
interface Settled<T> {
  value: T | null;
  failed: boolean;
}

// Resolve a promise to its value, or record failure — so one dead endpoint
// doesn't blank the whole tab, and so callers can tell a real failure apart
// from a genuinely empty result instead of treating both as "no data".
// Re-throws auth errors to trigger a login bounce.
async function settle<T>(p: Promise<T>): Promise<Settled<T>> {
  try {
    return { value: await p, failed: false };
  } catch (err) {
    if (err instanceof UnauthorizedError) throw err;
    return { value: null, failed: true };
  }
}

function fmtDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// Curated human labels for the raw event/step names the iOS app reports
// (AnalyticsEventType, Check ID App/Check ID/Analytics/AnalyticsEvent.swift).
// Anything not in this map falls back to prettyEvent()'s dumb underscore
// replacement — this map exists specifically to correct the events whose
// technical name would otherwise misrepresent what actually happened.
// fake_id_detection in particular fires on every scan that runs the AAMVA
// risk-scoring check (confidence_score + failedChecks), NOT only when a fake
// is actually caught — never label it as a count of detected fakes.
const EVENT_LABELS: Record<string, string> = {
  app_launched: 'App launched',
  app_session_duration: 'Session ended',
  did_record_scan_outcome: 'Recorded scan outcome',
  passport_verification_result: 'Passport verification result',
  did_restore_purchase: 'Restored purchase',
  did_dismiss_paywall: 'Dismissed paywall',
  onboarding_completed: 'Onboarding completed',
  camera_permission_result: 'Camera permission responded',
  did_scan_id: 'Scanned an ID',
  did_add_guest: 'Added guest',
  did_create_list: 'Created list',
  did_import_csv_guests: 'Imported guest list (CSV)',
  did_export_guest_list: 'Exported guest list',
  feature_blocked: 'Hit a Pro-only feature',
  upgrade_cta_tapped: 'Tapped upgrade',
  did_view_paywall: 'Viewed paywall',
  did_purchase_pro: 'Purchased Pro',
  did_view_business_paywall: 'Viewed Business paywall',
  did_start_business_trial: 'Started Business trial',
  did_purchase_business: 'Purchased Business',
  fake_id_detection: 'ID risk-scored (every scan, not confirmed fakes)',
  passback_detected: 'Passback flagged',
};

function prettyEvent(e: string): string {
  return EVENT_LABELS[e] ?? e.replace(/[._]/g, ' ');
}

// Raw AAMVA REAL ID indicator values (ScanEvent/AnalyticsEvent's
// real_id_compliance metadata key) mapped to the same labels used on the
// Overview page's "Scans by REAL ID indicator" panel.
const REAL_ID_LABELS: Record<string, string> = {
  COMPLIANT: 'Compliant',
  NON_COMPLIANT: 'Not compliant',
  NOT_ENCODED: 'Not encoded',
  NOT_REPORTED: 'Not reported',
};

function prettyRealId(v: string): string {
  return REAL_ID_LABELS[v] ?? v.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// Both methodBreakdown and realIdBreakdown are read from raw AnalyticsEvent
// metadata, which is purged after 7 days — so either panel reflects at most
// the last week even when a wider range is selected above.
const RAW_WINDOW_CAVEAT =
  'From received event metadata within the selected range. Coverage depends on the app version and available raw events.';

// Human names for the aggregation-service funnel types (see
// AggregationService.aggregateFunnels, API src/analytics/aggregation.service.ts).
// core_features is intentionally absent — it's excluded from this view (see
// FUNNEL_TYPES_SHOWN below), so it never needs a label.
const FUNNEL_TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  pro_monetization: 'Pro monetization',
  business_monetization: 'Business monetization',
};

// core_features (did_scan_id → did_add_guest → did_create_list) isn't a real
// user-intent funnel — those three events aren't a sequence anyone actually
// walks in order, so its "conversions" (e.g. 1% then 114%) are noise, not a
// signal. Dropped from this view entirely.
const FUNNEL_TYPES_SHOWN = new Set(['onboarding', 'pro_monetization', 'business_monetization']);

function prettyFunnel(t: string): string {
  return FUNNEL_TYPE_LABELS[t] ?? t.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// The aggregator counts, per day, distinct devices that fired each step's
// event name — independently per step, not devices that walked the full
// sequence in order. So a later "step" can legitimately have MORE devices
// than an earlier one (e.g. did_scan_id is reached via far more paths than
// just this funnel), which produced nonsensical >100% conversions in the raw
// server figures — funnelChart() clamps the displayed step-to-step % at 100.

// Business purchasing (IAP) is gated off pending the next App Review release
// — every business_monetization step reading zero across the whole window is
// the known, current, TRUE state, not a data problem. Rendering it as an
// all-zero funnel reads as broken; a quiet explanation is more honest.
function isUnlaunchedBusinessFunnel(f: FunnelResult): boolean {
  return f.key === 'business_monetization' && f.launched === false;
}


const DAILY_METRIC_OPTIONS: { label: string; value: DailySeriesMetric }[] = [
  { label: 'Active devices', value: 'activeDevices' },
  { label: 'Sessions', value: 'sessions' },
  { label: 'Scans', value: 'scans' },
  { label: 'New devices', value: 'newDevices' },
  { label: 'Pro purchases', value: 'proPurchases' },
];

// One plain sentence explaining exactly what each metric counts — see the
// pinned metric definitions in API's daily-series endpoint.
const DAILY_METRIC_CAPTIONS: Record<DailySeriesMetric, string> = {
  activeDevices: 'Distinct devices that reported any event that day.',
  sessions: 'Sessions ended that day.',
  scans: 'IDs scanned that day.',
  newDevices: 'Devices seen for the first time that day.',
  proPurchases: 'Pro purchases that day.',
};

// ── Daily activity (self-managing; own metric picker, independent of the
// page's date-range selector) — mirrors the Overview page's growth panel. ──
function dailyActivitySection(): HTMLElement {
  let metric: DailySeriesMetric = 'activeDevices';
  const seriesDays = 30;

  const controlsHost = el('div', { class: 'growth-controls' });
  const subCaption = el('p', { class: 'panel-sub' });
  const chartHost = el('div', { class: 'chart-host' }, [loadingBox()]);

  function renderControls(): void {
    controlsHost.replaceChildren(
      segmented(DAILY_METRIC_OPTIONS, metric, (v) => {
        metric = v as DailySeriesMetric;
        renderControls();
        void load();
      }),
    );
  }

  async function load(): Promise<void> {
    subCaption.textContent = DAILY_METRIC_CAPTIONS[metric];
    chartHost.replaceChildren(loadingBox());
    try {
      const s = await getDailySeries(metric, seriesDays);
      chartHost.replaceChildren(
        lineAreaChart(
          s.points.map((p) => ({ label: p.date.slice(0, 10), value: p.value })),
          { name: metric, format: (n) => fmtInt(n) },
        ),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      chartHost.replaceChildren(
        el('div', { class: 'banner banner-error' }, [
          err instanceof Error ? err.message : 'Failed to load.',
        ]),
      );
    }
  }

  renderControls();
  void load();
  return panel('Daily activity (overnight totals)', [controlsHost, subCaption, chartHost], { wide: true });
}

// Persistent freshness line, shown near the date-range selector regardless
// of whether the panels below are empty — an operator should be able to
// tell "stale by design" apart from "broken" without digging into an empty
// state. See getAnalyticsFreshness / AggregationController#getFreshness for
// why daily figures always lag: the aggregation cron runs once overnight.
function freshnessMessage(settled: Settled<AnalyticsFreshness>): string {
  if (settled.failed || !settled.value) {
    return 'Could not check data freshness — figures below may be stale without warning.';
  }
  const f = settled.value;
  const eventText = f.newestRawEventAt
    ? `newest event reported ${relativeTime(f.newestRawEventAt)}`
    : 'no events reported yet';
  const dailyText = f.newestDailyAggregationDate
    ? `daily totals current through ${fmtDay(f.newestDailyAggregationDate)}`
    : 'daily totals not yet computed';
  return `${eventText}; ${dailyText}. Daily totals run at 02:00 UTC for the previous UTC day. Live received activity above updates separately.`;
}

export async function renderAppAnalytics({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'app');

  let days = 30;
  const cap = caption('Received app activity across iOS, macOS and visionOS. Live reporting appears first; historical daily totals follow.');
  const rangeHost = el('div', { class: 'seg-host' });
  const head = el('div', { class: 'view-head' }, [
    el('div', { class: 'page-head' }, [el('h1', {}, ['App analytics'])]),
    rangeHost,
  ]);
  const freshnessHost = el('p', { class: 'view-caption' }, ['Checking data freshness…']);
  const body = el('div', {}, [loadingBox()]);
  content.append(head, cap, liveAnalyticsSection(), freshnessHost, dailyActivitySection(), body);

  function renderRange(): void {
    rangeHost.replaceChildren(
      segmented(
        [
          { label: '7 days', value: '7' },
          { label: '30 days', value: '30' },
          { label: '90 days', value: '90' },
        ],
        String(days),
        (v) => {
          days = Number(v);
          renderRange();
          void load();
        },
      ),
    );
  }

  async function load(): Promise<void> {
    body.replaceChildren(loadingBox());
    try {
      const [overview, scans, sessions, funnels, cohorts, subs, freshness] = await Promise.all([
        settle(getDashboardOverview(days)),
        settle(getScanStats(days)),
        settle(getSessionStats(days)),
        settle(getFunnels(days)),
        settle(getCohorts(days)),
        settle(getSubscriptionBreakdown(days)),
        settle(getAnalyticsFreshness()),
      ]);
      freshnessHost.textContent = freshnessMessage(freshness);
      const anyRequestFailed = [overview, scans, sessions, funnels, cohorts, subs].some(
        (s) => s.failed,
      );
      body.replaceChildren(
        renderKpis(overview.value, scans.value, sessions.value),
        renderPanels(overview.value, scans.value, funnels.value, cohorts.value, subs.value, {
          anyRequestFailed,
          freshness: freshness.value,
        }),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(body, err instanceof Error ? err.message : 'Failed to load.');
    }
  }

  renderRange();
  await load();
}

function renderKpis(
  overview: DashboardOverview | null,
  scans: ScanStats | null,
  sessions: SessionStats | null,
): HTMLElement {
  return kpiGrid([
    kpiCard('Events', overview ? fmtCompact(overview.keyMetrics.totalEvents) : '—', {
      sub: overview ? `${overview.keyMetrics.eventGrowth} vs prev` : undefined,
      sub2: 'all analytics events',
    }),
    kpiCard('Unique devices', overview ? fmtInt(overview.keyMetrics.uniqueDevices) : '—', {
      sub: overview ? `${overview.keyMetrics.deviceGrowth} vs prev` : undefined,
    }),
    kpiCard('Events / device', overview ? overview.keyMetrics.eventsPerDevice : '—'),
    kpiCard('Scans', scans ? fmtInt(scans.totalScans) : '—', {
      sub: scans ? `${fmtInt(scans.uniqueDevices)} devices` : undefined,
    }),
    kpiCard('Sessions', sessions ? fmtInt(sessions.totalSessions) : '—'),
    kpiCard('Avg session', sessions ? fmtDuration(sessions.averageDuration) : '—', {
      sub: '(from app session events)',
    }),
  ]);
}

function renderPanels(
  overview: DashboardOverview | null,
  scans: ScanStats | null,
  funnels: FunnelResult[] | null,
  cohorts: Cohort[] | null,
  subs: SubscriptionBreakdown | null,
  emptyContext: { anyRequestFailed: boolean; freshness: AnalyticsFreshness | null },
): HTMLElement {
  const panels: (HTMLElement | null)[] = [];

  if (overview?.topEvents?.length) {
    panels.push(
      panel('Top events', [
        barList(
          overview.topEvents.map((e) => ({ label: prettyEvent(e.event), value: e.count })),
        ),
      ]),
    );
  }

  const methodEntries = scans?.methodBreakdown ? Object.entries(scans.methodBreakdown) : [];
  if (methodEntries.length) {
    panels.push(
      panel(
        'Scans by method',
        [barList(methodEntries.map(([method, count]) => ({ label: method, value: count })))],
        { subtitle: RAW_WINDOW_CAVEAT },
      ),
    );
  }

  const realIdEntries = scans?.realIdBreakdown ? Object.entries(scans.realIdBreakdown) : [];
  if (realIdEntries.length) {
    panels.push(
      panel(
        'Scans by REAL ID indicator',
        [
          barList(
            realIdEntries.map(([code, count]) => ({
              label: prettyRealId(code),
              value: count,
            })),
          ),
        ],
        { subtitle: `As encoded on the document. ${RAW_WINDOW_CAVEAT}` },
      ),
    );
  }

  if (subs?.breakdown?.length) {
    const segs: Segment[] = subs.breakdown.map((b) => ({
      label: b.type.charAt(0).toUpperCase() + b.type.slice(1),
      value: b.users,
      color: SUB_COLORS[b.type] ?? '#8e8e93',
    }));
    panels.push(
      panel('Users by plan', [
        stackedBar(segs),
        el('p', { class: 'panel-note' }, [
          'Device-reported plan flags, not billing records — Business can appear before launch from internal/test devices.',
        ]),
      ]),
    );
  }

  const funnelPanels = (funnels ?? [])
    .filter((f) => f.steps?.length && FUNNEL_TYPES_SHOWN.has(f.key))
    .map((f) => {
      if (isUnlaunchedBusinessFunnel(f)) {
        return panel(prettyFunnel(f.key), [
          el('p', { class: 'panel-empty' }, [
            'Not launched yet — Business purchasing ships with the next app release.',
          ]),
        ]);
      }
      const children: (HTMLElement | null)[] = [
        funnelChart(
          f.steps.map((s) => ({ label: s.label || prettyEvent(s.event), value: s.devices })),
        ),
      ];
      // Entry signals that feed the funnel without being sequential stages
      // (e.g. free-limit hits and upgrade taps around the Pro paywall).
      if (f.context?.length) {
        children.push(
          el('p', { class: 'panel-note' }, [
            'Context: ' +
              f.context
                .map((c) => `${c.label || prettyEvent(c.event)} ${c.devices.toLocaleString('en-US')}`)
                .join(' · '),
          ]),
        );
      }
      return panel(prettyFunnel(f.key), children.filter((c): c is HTMLElement => !!c));
    });
  panels.push(...funnelPanels);

  if (cohorts?.length) {
    panels.push(panel('Retention by cohort', [cohortTable(cohorts)], { wide: true }));
  }

  const present = panels.filter((p): p is HTMLElement => !!p);
  const state = resolveEmptyPanelState({
    hasVisiblePanels: present.length > 0,
    anyRequestFailed: emptyContext.anyRequestFailed,
    freshness: emptyContext.freshness,
  });
  if (state.kind === 'has-data') {
    return el('div', { class: 'stack-gap' }, [panelGrid(present)]);
  }
  return emptyStateBanner(state);
}

// Three genuinely different reasons the panel grid can be empty — see
// app-analytics-empty-state.ts for why collapsing them into one message was
// the bug. A failed request must never render as "no data": that reads as
// the pipeline being empty when it's actually just unreachable right now.
function emptyStateBanner(state: Exclude<EmptyPanelState, { kind: 'has-data' }>): HTMLElement {
  if (state.kind === 'no-events-ever') {
    return el('div', { class: 'empty' }, [
      'No app analytics yet. Data appears once the iOS app reports events.',
    ]);
  }
  if (state.kind === 'not-yet-aggregated') {
    return el('div', { class: 'banner banner-warn', role: 'status' }, [
      `This window hasn't been aggregated yet — ${fmtInt(state.rawEventCountLast24h)} raw events reported in the last 24h, so the pipeline is alive. Daily totals run overnight; next run is ${fmtDay(state.nextRunAt)} at ${new Date(state.nextRunAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' })}. Try again after that, or widen the date range.`,
    ]);
  }
  return el('div', { class: 'banner banner-error', role: 'alert' }, [
    'Could not load analytics — the request failed. This is not the same as "no data"; try reloading the page.',
  ]);
}

const RETENTION_DAYS = [1, 7, 14, 30];

function cohortTable(cohorts: Cohort[]): HTMLElement {
  const rows = cohorts.slice(0, 10);
  const header = el('tr', {}, [
    el('th', {}, ['Cohort']),
    el('th', { class: 'num' }, ['Size']),
    ...RETENTION_DAYS.map((d) => el('th', { class: 'num' }, [`D${d}`])),
  ]);
  const body = rows.map((c) => {
    const byDay = new Map(
      (c.retention ?? []).map((r) => [r.daysSinceFirstSeen, r.retentionRate]),
    );
    return el('tr', {}, [
      el('td', {}, [c.cohortDate]),
      el('td', { class: 'num' }, [fmtInt(c.cohortSize)]),
      ...RETENTION_DAYS.map((d) => {
        const rate = byDay.get(d);
        return el('td', { class: 'num cell-muted' }, [rate ? `${rate}%` : '—']);
      }),
    ]);
  });
  return el('div', { class: 'table-wrap' }, [
    el('table', { class: 'data-table' }, [
      el('thead', {}, [header]),
      el('tbody', {}, body),
    ]),
  ]);
}
